import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { BackHeaderComponent } from '../back-header/back-header.component';
import { IconBadgeHeaderComponent } from '../icon-badge-header/icon-badge-header.component';
import { ConsentChannel, ConsentPurpose, CookiePreferences } from 'src/app/interfaces';
import { ConsentService } from 'src/app/services/consent.service';
import { LegalDocumentsService } from 'src/app/services/legal-documents.service';

/**
 * The permanent place to review, change or withdraw every optional consent.
 *
 * Reachable from the banner's "Configurar" and from the help section: withdrawal has to be at least
 * as easy as granting, so it cannot live only behind a banner that disappears once answered.
 *
 * The rows are two consents with different consequences — cookies act on this browser, marketing
 * email on the account — and each says so in its own title.
 */
@Component({
  selector: 'app-cookie-preferences',
  templateUrl: './cookie-preferences.component.html',
  standalone: true,
  imports: [
    CommonModule,
    // Neither toggle module is exported by SharedModule, so both are imported per-component.
    MatSlideToggleModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    BackHeaderComponent,
    IconBadgeHeaderComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookiePreferencesComponent {
  private readonly consent = inject(ConsentService);
  private readonly legalDocuments = inject(LegalDocumentsService);
  private readonly dialogRef = inject(MatDialogRef<CookiePreferencesComponent>);

  /** Working copy: nothing is applied until the user saves, so a stray tap is not a decision. */
  readonly analytics = signal(this.consent.cookiePreferences()?.analytics ?? false);
  readonly marketing = signal(this.consent.cookiePreferences()?.marketing ?? false);
  readonly marketingComms = signal(this.consent.isGranted(ConsentPurpose.MARKETING_COMMS));

  readonly isSaving = signal(false);

  close(): void {
    this.dialogRef.close();
  }

  /**
   * Whether the marketing-email row was touched here, so only a real interaction is recorded.
   * Writing the untouched default would turn "never asked" into "said no", and the post-purchase
   * prompt relies on telling those apart — the user would lose the chance to be asked at all.
   */
  private commsTouched = false;

  readonly isRetrying = signal(false);

  /**
   * The marketing-email row only makes sense signed in: there is no account to send to otherwise.
   *
   * Keyed off auth rather than the loaded state, which `ConsentService` leaves null on a failed
   * `/consents/me` — a withdrawal surface that silently drops one of its rows on a backend blip is
   * the wrong way to fail.
   */
  readonly showCommunications = this.consent.isSignedIn;

  /**
   * Signed in, but the account-level state never arrived, so the toggle's current position is
   * unknown. Shown disabled with a retry rather than defaulted, because rendering "off" would
   * misreport a grant as a refusal.
   */
  readonly communicationsUnavailable = computed(
    () => this.consent.isSignedIn() && this.consent.consentState() === null
  );

  /** Re-read the account-level consent so the row can show its real value. */
  async retryCommunications(): Promise<void> {
    if (this.isRetrying()) return;
    this.isRetrying.set(true);
    try {
      await this.consent.load(true);
      if (this.consent.consentState() !== null) {
        this.marketingComms.set(this.consent.isGranted(ConsentPurpose.MARKETING_COMMS));
      }
    } finally {
      this.isRetrying.set(false);
    }
  }

  /** Open the policy describing the processing these toggles govern. */
  openPrivacyPolicy(): void {
    this.legalDocuments.openDataPrivacyPolicy();
  }

  setMarketingComms(granted: boolean): void {
    this.commsTouched = true;
    this.marketingComms.set(granted);
  }

  save(): void {
    this.persist({ analytics: this.analytics(), marketing: this.marketing() });
  }

  /**
   * The convenience path. Deliberately limited to the cookie categories: the email consent sits
   * in its own section and bundling it behind a button labelled "accept all cookies" would not be
   * the specific consent the law requires.
   */
  acceptAllCookies(): void {
    this.analytics.set(true);
    this.marketing.set(true);
    this.persist({ analytics: true, marketing: true });
  }

  rejectAllCookies(): void {
    this.analytics.set(false);
    this.marketing.set(false);
    this.persist({ analytics: false, marketing: false });
  }

  /**
   * Apply the choice, then close on the *local* write rather than the server round trip.
   *
   * `setCookiePreferences` writes localStorage, the state signal and the tracking tags before its
   * first `await`, so the browser is already honouring the choice by the time it returns. The
   * account-level copy continues in the background, and `recordPurposes` treats a failed server
   * write as non-fatal for the same reason: the browser is what has to honour a cookie choice.
   */
  private persist(preferences: CookiePreferences): void {
    if (this.isSaving()) return;
    // Guards the synchronous stretch below, which ends at `close`: two taps in the same tick would
    // otherwise both reach the write. Nothing resets it because the component does not outlive the
    // close on the last line.
    this.isSaving.set(true);
    void this.consent
      .setCookiePreferences(preferences, ConsentChannel.SETTINGS)
      .then(() => {
        // Never write a value that could not be read: the toggle is disabled in that state, so
        // `commsTouched` is already false, and this keeps it true if the row becomes editable.
        if (this.showCommunications() && this.commsTouched && !this.communicationsUnavailable()) {
          return this.consent.setPurpose(
            ConsentPurpose.MARKETING_COMMS,
            this.marketingComms(),
            ConsentChannel.SETTINGS
          );
        }
        return undefined;
      })
      .catch(error => console.error('Could not record the consent preferences.', error));
    this.dialogRef.close(true);
  }
}
